<template>
  <div class="main-layout">
    <!-- 桌面/平板侧边栏 -->
    <aside class="sidebar" :class="{ 'sidebar--collapsed': isTablet }">
      <div class="sidebar__brand">
        <img src="/logo.png" alt="子恪CRM" class="sidebar__logo" />
        <span class="sidebar__title" v-show="!isTablet">子恪CRM</span>
      </div>

      <nav class="sidebar__nav">
        <router-link
          v-for="item in menuItems"
          :key="item.path"
          :to="item.path"
          class="sidebar__item"
          :class="{ 'sidebar__item--active': route.path === item.path }"
          :title="item.title"
        >
          <el-icon size="20"><component :is="iconMap[item.icon]" /></el-icon>
          <span class="sidebar__label" v-show="!isTablet">{{ item.title }}</span>
        </router-link>
      </nav>

      <div class="sidebar__footer" v-show="!isTablet">
        <span>子恪CRM v1.0</span>
      </div>
    </aside>

    <!-- 主内容区 -->
    <div class="main-area">
      <header class="header">
        <div class="header__left">
          <button class="header__menu-btn" @click="drawerVisible = true">
            <el-icon size="22"><Menu /></el-icon>
          </button>
          <h1 class="header__title">{{ pageTitle }}</h1>
        </div>

        <div class="header__right">
          <el-dropdown @command="handleCommand" trigger="click">
            <div class="user-info">
              <div class="user-info__avatar">
                {{ avatarText }}
              </div>
              <span class="user-info__name">{{ authStore.user?.nickname || authStore.user?.username }}</span>
              <el-icon class="user-info__arrow"><ArrowDown /></el-icon>
            </div>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item v-if="authStore.isSuperAdmin" command="settings">
                  <el-icon><Setting /></el-icon>
                  系统设置
                </el-dropdown-item>
                <el-dropdown-item v-if="authStore.user?.hasPassword" command="changePassword">
                  <el-icon><Lock /></el-icon>
                  修改密码
                </el-dropdown-item>
                <el-dropdown-item command="logout">
                  <el-icon><SwitchButton /></el-icon>
                  退出登录
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </header>

      <main class="main-content">
        <router-view />
      </main>
    </div>

    <!-- 手机端底部导航 -->
    <nav class="bottom-nav" v-if="isMobile">
      <router-link
        v-for="item in visibleMenuItems"
        :key="item.path"
        :to="item.path"
        class="bottom-nav__item"
        :class="{ 'bottom-nav__item--active': route.path === item.path }"
      >
        <el-icon size="22"><component :is="iconMap[item.icon]" /></el-icon>
        <span>{{ item.title }}</span>
      </router-link>
    </nav>

    <!-- 手机端抽屉菜单 -->
    <el-drawer
      v-model="drawerVisible"
      direction="ltr"
      size="260px"
      :with-header="false"
      class="mobile-drawer"
    >
      <div class="drawer__brand">
        <img src="/logo.png" alt="子恪CRM" />
        <span>子恪CRM</span>
      </div>
      <nav class="drawer__nav">
        <router-link
          v-for="item in menuItems"
          :key="item.path"
          :to="item.path"
          class="drawer__item"
          :class="{ 'drawer__item--active': route.path === item.path }"
          @click="drawerVisible = false"
        >
          <el-icon size="20"><component :is="iconMap[item.icon]" /></el-icon>
          <span>{{ item.title }}</span>
        </router-link>
      </nav>
    </el-drawer>

    <!-- 修改密码弹窗 -->
    <el-dialog v-model="passwordDialogVisible" title="修改密码" width="400px" :width="dialogWidth">
      <el-form :model="passwordForm" label-width="80px">
        <el-form-item label="旧密码">
          <el-input v-model="passwordForm.oldPassword" type="password" show-password placeholder="请输入旧密码" />
        </el-form-item>
        <el-form-item label="新密码">
          <el-input v-model="passwordForm.newPassword" type="password" show-password placeholder="请输入新密码" />
        </el-form-item>
        <el-form-item label="确认密码">
          <el-input v-model="passwordForm.confirmPassword" type="password" show-password placeholder="请再次输入新密码" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="passwordDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="passwordLoading" @click="handleChangePassword">确认</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import { useAuthStore } from '@/store/auth';
import {
  Odometer,
  OfficeBuilding,
  User,
  Postcard,
  Setting,
  ArrowDown,
  Menu,
  SwitchButton,
  Lock,
} from '@element-plus/icons-vue';

const route = useRoute();
const authStore = useAuthStore();
const drawerVisible = ref(false);
const passwordDialogVisible = ref(false);
const passwordLoading = ref(false);
const passwordForm = ref({
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
});

const iconMap = {
  Odometer,
  OfficeBuilding,
  User,
  Postcard,
  Setting,
};

const allMenuItems = [
  { path: '/dashboard', title: '仪表盘', icon: 'Odometer' },
  { path: '/enterprises', title: '企业', icon: 'OfficeBuilding', requireSuperAdmin: true },
  { path: '/customers', title: '客户', icon: 'Postcard' },
  { path: '/users', title: '用户', icon: 'User', requireAdmin: true },
  { path: '/settings', title: '设置', icon: 'Setting', requireSuperAdmin: true },
];

const menuItems = computed(() => allMenuItems.filter((item) => {
  if (item.requireSuperAdmin) return authStore.isSuperAdmin;
  if (item.requireAdmin) return authStore.isAdmin;
  return true;
}));

const visibleMenuItems = computed(() => menuItems.value.slice(0, 5));

const pageTitle = computed(() => route.meta.title || '子恪CRM');

const windowWidth = ref(window.innerWidth);
window.addEventListener('resize', () => {
  windowWidth.value = window.innerWidth;
  if (windowWidth.value > 768) {
    drawerVisible.value = false;
  }
});

const isTablet = computed(() => windowWidth.value <= 1024 && windowWidth.value > 768);
const isMobile = computed(() => windowWidth.value <= 768);
const dialogWidth = computed(() => (windowWidth.value <= 768 ? '90%' : '400px'));

const avatarText = computed(() => {
  const name = authStore.user?.nickname || authStore.user?.username || 'U';
  return name.charAt(0).toUpperCase();
});

const router = useRouter();

function handleCommand(command) {
  if (command === 'logout') {
    authStore.logout();
  } else if (command === 'settings') {
    router.push('/settings');
  } else if (command === 'changePassword') {
    passwordForm.value = { oldPassword: '', newPassword: '', confirmPassword: '' };
    passwordDialogVisible.value = true;
  }
}

async function handleChangePassword() {
  const { oldPassword, newPassword, confirmPassword } = passwordForm.value;
  if (!oldPassword || !newPassword || !confirmPassword) {
    return ElMessage.warning('请填写完整密码信息');
  }
  if (newPassword.length < 6) {
    return ElMessage.warning('新密码长度不能少于 6 位');
  }
  if (newPassword !== confirmPassword) {
    return ElMessage.warning('两次输入的新密码不一致');
  }

  passwordLoading.value = true;
  try {
    await authStore.changePassword(oldPassword, newPassword);
    ElMessage.success('密码修改成功');
    passwordDialogVisible.value = false;
  } finally {
    passwordLoading.value = false;
  }
}
</script>

<style scoped lang="scss">
.main-layout {
  display: flex;
  height: 100vh;
  background: var(--zk-bg);
}

.sidebar {
  width: var(--zk-sidebar-width);
  flex-shrink: 0;
  background: var(--zk-bg-elevated);
  border-right: 1px solid var(--zk-border);
  display: flex;
  flex-direction: column;
  transition: width var(--zk-transition);

  &--collapsed {
    width: var(--zk-sidebar-collapsed);

    .sidebar__brand {
      justify-content: center;
      padding: 0;
    }

    .sidebar__logo {
      margin-right: 0;
    }

    .sidebar__item {
      justify-content: center;
      padding: 0;
    }
  }

  &__brand {
    height: var(--zk-header-height);
    display: flex;
    align-items: center;
    padding: 0 24px;
    border-bottom: 1px solid var(--zk-border);
  }

  &__logo {
    width: 36px;
    height: 36px;
    margin-right: 12px;
    object-fit: contain;
  }

  &__title {
    font-family: var(--zk-font-display);
    font-size: 20px;
    font-weight: 700;
    background: var(--zk-gradient-linear);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  &__nav {
    flex: 1;
    padding: 16px 12px;
    overflow-y: auto;
  }

  &__item {
    display: flex;
    align-items: center;
    padding: 14px 16px;
    margin-bottom: 8px;
    border-radius: var(--zk-radius-sm);
    color: var(--zk-text-secondary);
    text-decoration: none;
    transition: all var(--zk-transition-fast);

    .el-icon {
      flex-shrink: 0;
    }

    &:hover {
      background: var(--zk-gradient-soft);
      color: var(--zk-primary);
    }

    &--active {
      background: var(--zk-gradient-linear);
      color: white;
      box-shadow: 0 4px 16px rgba(0, 102, 255, 0.25);

      &:hover {
        background: var(--zk-gradient-linear);
        color: white;
      }
    }
  }

  &__label {
    margin-left: 12px;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
  }

  &__footer {
    padding: 16px;
    font-size: 12px;
    color: var(--zk-text-tertiary);
    text-align: center;
    border-top: 1px solid var(--zk-border);
  }
}

.main-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}

.header {
  height: var(--zk-header-height);
  background: var(--zk-bg-elevated);
  border-bottom: 1px solid var(--zk-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  flex-shrink: 0;

  &__left {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__menu-btn {
    display: none;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 10px;
    background: var(--zk-bg);
    color: var(--zk-text-secondary);
    cursor: pointer;
    transition: all var(--zk-transition-fast);

    &:hover {
      background: var(--zk-gradient-soft);
      color: var(--zk-primary);
    }
  }

  &__title {
    margin: 0;
    font-family: var(--zk-font-display);
    font-size: 20px;
    font-weight: 700;
    color: var(--zk-text-primary);
  }
}

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: var(--zk-radius-sm);
  transition: background var(--zk-transition-fast);

  &:hover {
    background: var(--zk-bg);
  }

  &__avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--zk-gradient-linear);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 600;
  }

  &__name {
    font-size: 14px;
    font-weight: 500;
    color: var(--zk-text-primary);
  }

  &__arrow {
    color: var(--zk-text-tertiary);
  }
}

.main-content {
  flex: 1;
  overflow-y: auto;
  background: var(--zk-bg);
}

.bottom-nav {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: var(--zk-bottom-nav-height);
  background: var(--zk-bg-elevated);
  border-top: 1px solid var(--zk-border);
  display: flex;
  align-items: center;
  justify-content: space-around;
  z-index: 100;
  padding-bottom: env(safe-area-inset-bottom);

  &__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    flex: 1;
    height: 100%;
    color: var(--zk-text-tertiary);
    text-decoration: none;
    font-size: 11px;
    transition: all var(--zk-transition-fast);

    .el-icon {
      transition: transform var(--zk-transition-fast);
    }

    &:hover {
      color: var(--zk-primary);
    }

    &--active {
      color: var(--zk-primary);

      .el-icon {
        transform: translateY(-2px);
      }
    }
  }
}

.drawer__brand {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 20px;
  border-bottom: 1px solid var(--zk-border);

  img {
    width: 40px;
    height: 40px;
    object-fit: contain;
  }

  span {
    font-family: var(--zk-font-display);
    font-size: 22px;
    font-weight: 700;
    background: var(--zk-gradient-linear);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
}

.drawer__nav {
  padding: 16px;
}

.drawer__item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  margin-bottom: 8px;
  border-radius: var(--zk-radius-sm);
  color: var(--zk-text-secondary);
  text-decoration: none;
  transition: all var(--zk-transition-fast);

  &:hover {
    background: var(--zk-gradient-soft);
    color: var(--zk-primary);
  }

  &--active {
    background: var(--zk-gradient-linear);
    color: white;

    &:hover {
      background: var(--zk-gradient-linear);
      color: white;
    }
  }
}

// 平板适配
@media (max-width: 1024px) {
  .sidebar__label {
    display: none;
  }
}

// 手机适配
@media (max-width: 768px) {
  .sidebar {
    display: none;
  }

  .header {
    padding: 0 16px;

    &__menu-btn {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &__title {
      font-size: 18px;
    }
  }

  .user-info__name {
    display: none;
  }

  .main-content {
    padding-bottom: var(--zk-bottom-nav-height);
  }
}
</style>
